import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

/**
 * Visible breadcrumb trail rendered above the H1 of long-form pages.
 *
 * Mirrors the exact `items` array fed to `breadcrumbSchema()` in the same
 * route's structured data — Google's mobile SERPs then render the visible
 * crumbs as the URL preview (instead of the raw URL), which lifts CTR on
 * deep programmatic pages.
 *
 * Items: [{ name: "Home", path: "/" }, { name: "Guides", path: "/guides" }, ...]
 * The last item is rendered as plain text (current page — no link).
 */
export default function Breadcrumbs({ items }) {
  if (!items || items.length < 2) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      className="text-sm"
      data-testid="breadcrumbs"
    >
      <ol className="flex flex-wrap items-center gap-1.5 text-slate-500">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          const isHome = idx === 0;
          return (
            <li key={`${item.path}-${idx}`} className="flex items-center gap-1.5">
              {idx > 0 && (
                <ChevronRight
                  className="h-3.5 w-3.5 shrink-0 text-slate-300"
                  aria-hidden="true"
                />
              )}
              {isLast ? (
                <span
                  className="font-medium text-slate-700"
                  aria-current="page"
                  data-testid={`breadcrumb-current`}
                >
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className="inline-flex items-center gap-1 text-slate-500 transition hover:text-green-700"
                  data-testid={`breadcrumb-link-${idx}`}
                >
                  {isHome && <Home className="h-3.5 w-3.5" aria-hidden="true" />}
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
